import { Drawer } from 'vaul'
import { ReactNode, useState, forwardRef, useEffect, useRef, type KeyboardEvent } from 'react'
import { updateAppStatusBar } from '../../utils/appBridge'
import { getStatusBarColor, getStatusBarTheme } from '../../utils/sessionStorage'

const snapPoints = [1]

interface VaulDrawerProps {
  open: boolean
  setOpen: (open: boolean) => void
  children: ReactNode
  zIndex?: number
  onDismiss?: () => void
  className?: string
  dismissible?: boolean
  /** Accessible name for the dialog when the sheet does not use BottomSheetContent */
  title?: string
  /** Prefer labelling via a visible heading id to avoid a duplicate sr-only title focus stop (Power Select_B24) */
  titleId?: string
  /** Hide dismiss overlay from AT while keeping visual dim (e.g. parent sheet when nested dialog is open). */
  overlayHiddenFromAccessibility?: boolean
  /** Hide drawer content from AT while keeping it visible (parent sheet behind nested dialog). */
  contentHiddenFromAccessibility?: boolean
  /** Focus the dialog surface on open so TalkBack/VoiceOver enter the sheet, and return focus to the opener on close. */
  focusDrawerOnOpen?: boolean
}

const DrawerContent = forwardRef<
  HTMLDivElement,
  {
    children: ReactNode
    zIndex?: number
    setOpen: (open: boolean) => void
    onDismiss?: () => void
    title?: string
    titleId?: string
    dismissible?: boolean
    overlayHiddenFromAccessibility?: boolean
    contentHiddenFromAccessibility?: boolean
    className?: string
    onCloseAutoFocus: (event: Event) => void
  }
>(({ children, zIndex, setOpen, onDismiss, title, titleId, dismissible = true, overlayHiddenFromAccessibility = false, contentHiddenFromAccessibility = false, className, onCloseAutoFocus }, ref) => {
  const [snap] = useState<number | string | null>(snapPoints[0])

  const dismissSheet = () => {
    setOpen(false)
    onDismiss?.()
  }

  const overlayDismissible = dismissible && !overlayHiddenFromAccessibility

  return (
    <>
      <Drawer.Overlay
        // Power select_B27 / B29 — dismiss control: button role + name + hint (when dismissible)
        {...(overlayDismissible
          ? {
            role: 'button' as const,
            tabIndex: 0,
            'aria-label': 'Dismiss dialog',
            'aria-description': 'double tap to dismiss dialog',
            onKeyDown: (e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                e.stopPropagation()
                dismissSheet()
              }
            }
          }
          : { 'aria-hidden': true as const, tabIndex: -1 })}
        className='fixed inset-0 bg-black/35'
        style={{
          zIndex: zIndex ? zIndex - 1 : undefined,
          pointerEvents: 'auto'
        }}
        onClick={(e) => {
          e.stopPropagation()
          if (!dismissible) return
          dismissSheet()
        }}
      />
      <Drawer.Content
        ref={ref}
        className={`bg-white flex flex-col rounded-t-xl mt-24 
        h-fit max-h-[90%] fixed bottom-0 left-0 right-0 outline-none ${className ?? ''}`}
        style={{
          zIndex,
          pointerEvents: 'auto'
        }}
        onCloseAutoFocus={onCloseAutoFocus}
        role='dialog'
        aria-modal='true'
        // Not focusable: TalkBack makes a named focusable dialog its own swipe stop and reads the whole sheet (Taxation Rules_B03).
        // Explicit undefined overrides Radix FocusScope's default tabIndex=-1.
        tabIndex={undefined}
        // Prefer visible heading id for dialog name (Power select_B15 / Taxation Rules pattern)
        {...(titleId ? { 'aria-labelledby': titleId } : title ? { 'aria-label': title } : {})}
        {...(contentHiddenFromAccessibility ? { 'aria-hidden': true as const, inert: true as const } : {})}
      >
        {/* When titleId is set, keep an empty hidden title for Vaul and let the visible heading provide the name. */}
        {titleId ? (
          <Drawer.Title aria-hidden='true' className='sr-only'></Drawer.Title>
        ) : title ? (
          <Drawer.Title className='sr-only'>{title}</Drawer.Title>
        ) : (
          <Drawer.Title aria-hidden='true' className='sr-only'></Drawer.Title>
        )}
        <Drawer.Handle
          aria-hidden='true'
          className='!w-12 !h-1 !-top-[10px] !left-1/2 !-translate-x-1/2 
        !mx-auto !bg-white !opacity-100 !absolute'
        />
        <div
          className={`${snap === 1 ? 'overflow-y-auto' : 'overflow-hidden'} overscroll-none no-scrollbar rounded-t-xl`}
          style={{ touchAction: 'pan-y', overscrollBehavior: 'none' }}
        >
          {children}
        </div>
      </Drawer.Content>
    </>
  )
})

DrawerContent.displayName = 'DrawerContent'

export default function VaulDrawer({
  open,
  setOpen,
  children,
  zIndex = 10000,
  onDismiss,
  dismissible = true,
  title,
  titleId,
  className,
  overlayHiddenFromAccessibility = false,
  contentHiddenFromAccessibility = false,
  focusDrawerOnOpen = false
}: VaulDrawerProps) {
  const drawerContentRef = useRef<HTMLDivElement>(null)
  const openerRef = useRef<HTMLElement | null>(null)

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setOpen(false)
      onDismiss?.()
    }
  }

  useEffect(() => {
    if (open) {
      updateAppStatusBar('BFBFBF', 'dark', false)
    } else {
      updateAppStatusBar(getStatusBarColor() ?? 'FFFFFF', getStatusBarTheme() ?? 'dark')
    }
  }, [open])

  useEffect(() => {
    if (!open || !focusDrawerOnOpen) return

    // Vaul disables Radix open auto-focus, so activeElement is still the control that opened the sheet
    if (document.activeElement instanceof HTMLElement && !drawerContentRef.current?.contains(document.activeElement)) {
      openerRef.current = document.activeElement
    }

    const focusTarget = () => {
      // Stop re-focusing once the user has moved to a control inside the sheet
      const activeElement = document.activeElement
      const drawerContent = drawerContentRef.current
      if (
        drawerContent &&
        activeElement &&
        activeElement !== drawerContent &&
        activeElement.id !== titleId &&
        drawerContent.contains(activeElement)
      ) {
        return
      }
      if (titleId) {
        const heading = document.getElementById(titleId)
        if (heading) {
          heading.focus({ preventScroll: true })
          return
        }
      }
      drawerContentRef.current?.focus({ preventScroll: true })
    }

    focusTarget()
    // Vaul slides the sheet in over 500ms; TalkBack/VoiceOver ignore focus on off-screen nodes, so re-assert once it has settled
    const timers = [0, 50, 150, 300, 550].map((delay) => window.setTimeout(focusTarget, delay))
    return () => timers.forEach((timer) => window.clearTimeout(timer))
  }, [open, focusDrawerOnOpen, titleId])

  // Radix only restores focus to Drawer.Trigger (unused here), which leaves focus on <body> after close
  const handleCloseAutoFocus = (event: Event) => {
    const opener = openerRef.current
    openerRef.current = null
    if (!focusDrawerOnOpen || !opener?.isConnected) return
    event.preventDefault()
    opener.focus({ preventScroll: true })
  }

  return (
    <Drawer.Root
      dismissible={dismissible}
      snapPoints={snapPoints}
      open={open}
      onOpenChange={handleOpenChange}
      closeThreshold={0.9}
      scrollLockTimeout={0}
      disablePreventScroll={true}
    >
      <Drawer.Portal>
        <DrawerContent
          ref={drawerContentRef}
          zIndex={zIndex}
          setOpen={setOpen}
          onDismiss={onDismiss}
          title={title}
          titleId={titleId}
          dismissible={dismissible}
          className={className}
          overlayHiddenFromAccessibility={overlayHiddenFromAccessibility}
          contentHiddenFromAccessibility={contentHiddenFromAccessibility}
          onCloseAutoFocus={handleCloseAutoFocus}
        >
          {children}
        </DrawerContent>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
